import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../../types/chat';
import mcpApi, { MCPServer, MCPTestConnectionResult } from '../../services/mcpApi';
import { View, extractErrorDetail } from './types';
import { ServerListView } from './ServerListView';
import { ServerDetailsView } from './ServerDetailsView';
import { ServerFormView } from './ServerFormView';

interface MCPServersModalProps {
  project: DevProject | null;
  onClose: () => void;
  /** True while a build is running — edit/delete/toggle are disabled so a
   *  running build's MCP tools aren't invalidated mid-flight. */
  isBuilding?: boolean;
}

/** Slide-over panel to manage a project's MCP servers (list / details / add-edit). */
export const MCPServersModal: React.FC<MCPServersModalProps> = ({ project, onClose, isBuilding = false }) => {
  const { projectId } = useParams();
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [listActionError, setListActionError] = useState<string | null>(null);

  useEffect(() => {
    fetchServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchServers = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await mcpApi.listServers(projectId);
      setServers(data.servers || []);
    } catch (e) {
      console.error('Failed to fetch MCP servers:', e);
      setError('Failed to load MCP servers');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleToggle = async (server: MCPServer) => {
    if (!projectId) return;
    try {
      setListActionError(null);
      setTogglingId(server.id);
      const updated = await mcpApi.toggleServer(projectId, server.id, !server.enabled);
      setServers((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setView((v) =>
        v.kind === 'details' && v.server.id === updated.id ? { kind: 'details', server: updated } : v
      );
    } catch (e: any) {
      setListActionError(extractErrorDetail(e, 'Failed to toggle server'));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (server: MCPServer) => {
    if (!projectId) return;
    if (!window.confirm(`Delete MCP server "${server.name}"? Its stored credentials will be removed.`)) {
      return;
    }
    try {
      setListActionError(null);
      setDeletingId(server.id);
      await mcpApi.deleteServer(projectId, server.id);
      setServers((prev) => prev.filter((s) => s.id !== server.id));
      setView({ kind: 'list' });
    } catch (e: any) {
      setListActionError(extractErrorDetail(e, 'Failed to delete server'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleTestSaved = async (server: MCPServer): Promise<MCPTestConnectionResult | null> => {
    if (!projectId) return null;
    try {
      setTestingId(server.id);
      const result = await mcpApi.testServer(projectId, server.id);
      // Test persists status/tool info on the server — refresh from the backend
      const data = await mcpApi.listServers(projectId);
      setServers(data.servers || []);
      const refreshed = data.servers.find((s) => s.id === server.id);
      if (refreshed) {
        setView((v) =>
          v.kind === 'details' && v.server.id === server.id
            ? { kind: 'details', server: refreshed }
            : v
        );
      }
      return result;
    } catch (e: any) {
      setListActionError(extractErrorDetail(e, 'Test Connection failed'));
      return null;
    } finally {
      setTestingId(null);
    }
  };

  // After add/edit: refresh, then land OAuth servers on their details view so
  // the Sign in button is immediately visible (OAuth was detected server-side).
  const handleSaved = async (saved: MCPServer) => {
    if (!projectId) {
      setView({ kind: 'list' });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await mcpApi.listServers(projectId);
      setServers(data.servers || []);
      const refreshed = data.servers.find((s) => s.id === saved.id);
      setView(refreshed?.oauth ? { kind: 'details', server: refreshed } : { kind: 'list' });
    } catch (e) {
      console.error('Failed to fetch MCP servers:', e);
      setError('Failed to load MCP servers');
      setView({ kind: 'list' });
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch servers and sync the open details view (used after OAuth sign-in).
  const syncServer = async (serverId: string) => {
    if (!projectId) return;
    try {
      const data = await mcpApi.listServers(projectId);
      setServers(data.servers || []);
      const refreshed = data.servers.find((s) => s.id === serverId);
      if (refreshed) {
        setView((v) =>
          v.kind === 'details' && v.server.id === serverId
            ? { kind: 'details', server: refreshed }
            : v
        );
      }
    } catch (e) {
      console.error('Failed to refresh MCP servers:', e);
    }
  };

  const headerTitle =
    view.kind === 'form'
      ? view.server
        ? 'Edit MCP Server'
        : 'Add MCP Server'
      : view.kind === 'details'
      ? view.server.name
      : 'MCP Servers';

  const headerSubtitle =
    view.kind === 'form'
      ? 'Enter the connection details for the remote MCP server'
      : view.kind === 'details'
      ? view.server.description || ''
      : 'Connect external tools to Build mode via MCP';

  return (
    <div
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[480px] flex flex-col border-l border-border-secondary-alt ${
          isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow:
            '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="bg-bg-primary box-border flex gap-[var(--spacing-md,8px)] items-start p-[var(--spacing-3xl,24px)] w-full">
          <div className="flex flex-[1_0_0] gap-[var(--spacing-md,8px)] items-start min-h-px min-w-px">
            {view.kind !== 'list' && (
              <button
                onClick={() => setView({ kind: 'list' })}
                className="flex items-center justify-center rounded-full w-8 h-8 text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200 shrink-0"
                title="Back"
              >
                <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
              </button>
            )}
            <div className="flex flex-[1_0_0] flex-col gap-[var(--spacing-xxs,2px)] items-start min-h-px min-w-px whitespace-pre-wrap">
              <Text variant="text-lg" weight="semibold" color="primary" className="w-full">
                {headerTitle}
              </Text>
              <Text variant="text-sm" weight="regular" color="secondary" className="w-full break-words">
                {headerSubtitle}
              </Text>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded-full w-8 h-8 text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200 absolute right-[12px] top-[12px]"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="box-border flex flex-col gap-[16px] px-[var(--spacing-3xl,24px)] py-[var(--spacing-xl,16px)] w-full flex-1 overflow-y-auto">
          {view.kind === 'list' && (
            <ServerListView
              loading={loading}
              error={error}
              servers={servers}
              togglingId={togglingId}
              deletingId={deletingId}
              actionError={listActionError}
              isBuilding={isBuilding}
              onRetry={fetchServers}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onOpenDetails={(server) => setView({ kind: 'details', server })}
              onAddCustom={() => setView({ kind: 'form' })}
              project={project}
            />
          )}

          {view.kind === 'details' && (
            <ServerDetailsView
              server={view.server}
              testing={testingId === view.server.id}
              deleting={deletingId === view.server.id}
              isBuilding={isBuilding}
              onTest={() => handleTestSaved(view.server)}
              onEdit={() => setView({ kind: 'form', server: view.server })}
              onToggle={() => handleToggle(view.server)}
              onDelete={() => handleDelete(view.server)}
              onRefresh={() => syncServer(view.server.id)}
              project={project}
            />
          )}

          {view.kind === 'form' && projectId && (
            <ServerFormView
              projectId={projectId}
              server={view.server}
              existingServers={servers}
              isBuilding={isBuilding}
              onSaved={handleSaved}
              onCancel={() =>
                setView(view.server ? { kind: 'details', server: view.server } : { kind: 'list' })
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MCPServersModal;
